import React, { useState, useRef, useEffect } from 'react';
import './WorkspaceTreeNav.css';

const WorkspaceTreeNav = ({
  userId,
  onFilterByTask,
  toggleStackExpansion,
  handleFilterByProject,
  handleFilterByWorkspace,
  handleFilterByJob
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedWorkspace, setExpandedWorkspace] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedJob, setExpandedJob] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const dropdownRef = useRef(null);

  // Store projects, jobs, and tasks data
  const [projectsData, setProjectsData] = useState({});
  const [jobsData, setJobsData] = useState({});
  const [tasksData, setTasksData] = useState({});
  const [loadingProjects, setLoadingProjects] = useState({});
  const [loadingJobs, setLoadingJobs] = useState({});
  const [loadingTasks, setLoadingTasks] = useState({});

  const apiUrl = `${process.env.REACT_APP_API_BASE_URL}/api`;

  // Fetch workspaces when dropdown is opened
  const fetchWorkspaces = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${apiUrl}/Filters/GetFilteredWorkspace/${userId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "Filtered Workspace Data retrieved successfully" && data.jobs) {
        // Transform the workspaces from the jobs array
        const workspacesList = data.jobs.map(workspace => ({
          id: workspace.workspaceId,
          name: workspace.workspaceName,
          projectCount: workspace.projectCount || 0
        }));
        
        setWorkspaces(workspacesList);
      } else {
        setWorkspaces([]);
        setError("No workspaces found");
      }
    } catch (err) {
      console.error("Error fetching workspaces:", err);
      setError("Failed to load workspaces");
      setWorkspaces([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch projects for a specific workspace
  const fetchProjects = async (workspaceId) => {
    setLoadingProjects(prev => ({ ...prev, [workspaceId]: true }));
    try {
      const response = await fetch(`${apiUrl}/Filters/GetFilteredProject/${userId}?WorkspaceId=${workspaceId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "filtered project retrieved successfully" && data.jobs) {
        // Transform the projects from the jobs array
        const projects = data.jobs.map(project => ({
          id: project.projectId,
          name: project.projectName,
          workspaceId: workspaceId,
          jobCount: project.jobCount || 0
        }));
        
        setProjectsData(prev => ({
          ...prev,
          [workspaceId]: projects
        }));
      } else {
        setProjectsData(prev => ({
          ...prev,
          [workspaceId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching projects:", err);
      setProjectsData(prev => ({
        ...prev,
        [workspaceId]: []
      }));
    } finally {
      setLoadingProjects(prev => ({ ...prev, [workspaceId]: false }));
    }
  };

  // Fetch jobs for a specific project
  const fetchJobs = async (projectId) => {
    setLoadingJobs(prev => ({ ...prev, [projectId]: true }));
    try {
      const response = await fetch(`${apiUrl}/Filters/GetFilteredJob/${userId}?ProjectId=${projectId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "filtered job retrieved successfully" && data.jobs) {
        // Transform the jobs from the response
        const jobs = data.jobs.map(job => ({
          id: job.jobId,
          name: job.jobName,
          projectId: projectId,
          taskCount: job.taskCount || 0
        }));
        
        setJobsData(prev => ({
          ...prev,
          [projectId]: jobs
        }));
      } else {
        setJobsData(prev => ({
          ...prev,
          [projectId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching jobs:", err);
      setJobsData(prev => ({
        ...prev,
        [projectId]: []
      }));
    } finally {
      setLoadingJobs(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Fetch tasks for a specific job (keeping the existing logic)
  const fetchTasks = async (jobId) => {
    setLoadingTasks(prev => ({ ...prev, [jobId]: true }));
    try {
      const response = await fetch(`${apiUrl}/Filters/GetFilteredTask/${userId}?JobId=${jobId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.message === "Filtered tasks retrieved successfully" && data.tasks) {
        // Transform the tasks array
        const tasks = data.tasks.map(task => ({
          id: task.taskId,
          name: task.taskTitle,
          jobId: jobId
        }));
        
        setTasksData(prev => ({
          ...prev,
          [jobId]: tasks
        }));
      } else {
        setTasksData(prev => ({
          ...prev,
          [jobId]: []
        }));
      }
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setTasksData(prev => ({
        ...prev,
        [jobId]: []
      }));
    } finally {
      setLoadingTasks(prev => ({ ...prev, [jobId]: false }));
    }
  };

  // Handle workspace click - fetch projects
  const handleWorkspaceClick = async (workspaceId) => {
    if (expandedWorkspace === workspaceId) {
      // Collapse if already expanded
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand and fetch projects if not already loaded
      setExpandedWorkspace(workspaceId);
      setExpandedProject(null);
      setExpandedJob(null);
      
      // Fetch projects if not already loaded
      if (!projectsData[workspaceId]) {
        await fetchProjects(workspaceId);
      }
    }
  };

  // Handle workspace double-click
  const handleWorkspaceDoubleClick = (e, workspace) => {
    e.stopPropagation(); // Prevent expanding the stack
    if (workspace.id && handleFilterByWorkspace) {
      handleFilterByWorkspace(workspace.id, workspace.name);
    }
  };

  // Handle project click - fetch jobs
  const handleProjectClick = async (projectId) => {
    if (expandedProject === projectId) {
      // Collapse if already expanded
      setExpandedProject(null);
      setExpandedJob(null);
    } else {
      // Expand and fetch jobs if not already loaded
      setExpandedProject(projectId);
      setExpandedJob(null);
      
      // Fetch jobs if not already loaded
      if (!jobsData[projectId]) {
        await fetchJobs(projectId);
      }
    }
  };

  // Handle project double-click
  const handleProjectDoubleClick = (e, project) => {
    e.stopPropagation(); // Prevent expanding the stack
    if (project.id && handleFilterByProject) {
      handleFilterByProject(project.id, project.name);
    }
  };

  // Handle job click - fetch tasks
  const handleJobClick = async (jobId) => {
    if (expandedJob === jobId) {
      // Collapse if already expanded
      setExpandedJob(null);
    } else {
      // Expand and fetch tasks if not already loaded
      setExpandedJob(jobId);
      
      // Fetch tasks if not already loaded
      if (!tasksData[jobId]) {
        await fetchTasks(jobId);
      }
    }
  };

  // Handle job double-click
  const handleJobDoubleClick = (e, job) => {
   e.stopPropagation(); // Prevent expanding the stack
    if (job.id && handleFilterByProject) {
      handleFilterByJob(job.id, job.name);
    }
  };

  // Handle task click
  const handleTaskClick = async (task, job, project, workspace) => {
    console.log('Selected task:', task, 'for job:', job);
    
    // Find the complete job object with all necessary data
    const completeJob = {
      jobId: job.id,
      jobName: job.name,
      workspaceId: workspace?.id,
      workspaceName: workspace?.name,
      projectId: project?.id,
      projectName: project?.name,
    };
    
    // Call the callback with task and job info
    if (onFilterByTask) {
      onFilterByTask(task.id, task.name, completeJob);
    }
    
    // Close the dropdown
    setIsOpen(false);
    
    // Reset expanded states
    setExpandedWorkspace(null);
    setExpandedProject(null);
    setExpandedJob(null);
  };

  // Helper function to get workspace by ID
  const getWorkspaceById = (workspaceId) => {
    return workspaces.find(w => w.id === workspaceId);
  };

  // Helper function to get project by ID and workspace
  const getProjectById = (workspaceId, projectId) => {
    const projects = projectsData[workspaceId] || [];
    return projects.find(p => p.id === projectId);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        // Reset expanded states when closing
        setExpandedWorkspace(null);
        setExpandedProject(null);
        setExpandedJob(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Reset expanded states when opening
      setExpandedWorkspace(null);
      setExpandedProject(null);
      setExpandedJob(null);
      // Fetch workspaces when opening dropdown
      fetchWorkspaces();
    }
  };

  return (
    <div className="workspace-tree-nav" ref={dropdownRef}>
      <button
        className="workspace-toggle-btn"
        onClick={toggleDropdown}
        title="Workspace Navigation"
      >
        <i className="fas fa-layer-group" />
        <i className={`fas fa-chevron-${isOpen ? 'up' : 'down'} toggle-icon`} />
      </button>

      {isOpen && (
        <div className="workspace-dropdown slide-in-left">
          <div className="workspace-header">
            <i className="fas fa-sitemap" />
            <span>Workspace Navigation</span>
          </div>
          
          <div className="workspace-tree-content">
            {loading ? (
              <div className="loading-state">
                <i className="fas fa-spinner fa-spin" />
                <span>Loading workspaces...</span>
              </div>
            ) : error ? (
              <div className="error-state">
                <i className="fas fa-exclamation-circle" />
                <span>{error}</span>
                <button onClick={fetchWorkspaces} className="retry-btn">
                  <i className="fas fa-redo" />
                  Retry
                </button>
              </div>
            ) : workspaces.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-building" />
                <span>No workspaces found</span>
              </div>
            ) : (
              workspaces.map((workspace) => {
                const workspaceProjects = projectsData[workspace.id] || [];
                const isLoadingProjects = loadingProjects[workspace.id];
                
                return (
                  <div key={workspace.id} className="workspace-node">
                    <div
                      className="workspace-item"
                      onClick={() => handleWorkspaceClick(workspace.id)}
                      onDoubleClick={(e) => handleWorkspaceDoubleClick(e, workspace)}
                    >
                      <i className={`fas fa-chevron-${expandedWorkspace === workspace.id ? 'down' : 'right'} expand-icon`} />
                      <i className="fas fa-building workspace-icon" />
                      <div className="workspace-info">
                        <div className="workspace-name">{workspace.name}</div>
                      </div>
                      <span className="project-count">
                        ({workspace.projectCount || workspaceProjects.length})
                      </span>
                    </div>
                    
                    {expandedWorkspace === workspace.id && (
                      <div className="projects-container">
                        {isLoadingProjects ? (
                          <div className="loading-projects">
                            <i className="fas fa-spinner fa-spin" />
                            <span>Loading projects...</span>
                          </div>
                        ) : workspaceProjects.length === 0 ? (
                          <div className="no-projects-message">
                            <i className="fas fa-project-diagram" />
                            <span>No projects available</span>
                          </div>
                        ) : (
                          workspaceProjects.map((project) => {
                            const projectJobs = jobsData[project.id] || [];
                            const isLoadingJobs = loadingJobs[project.id];
                            
                            return (
                              <div key={project.id} className="project-node">
                                <div
                                  className="project-item"
                                  onClick={() => handleProjectClick(project.id)}
                                  onDoubleClick={(e) => handleProjectDoubleClick(e, project)}
                                >
                                  <i className={`fas fa-chevron-${expandedProject === project.id ? 'down' : 'right'} expand-icon`} />
                                  <i className="fas fa-project-diagram project-icon" />
                                  <span className="project-name">{project.name}</span>
                                  <span className="job-count">
                                    ({project.jobCount || projectJobs.length})
                                  </span>
                                </div>
                                
                                {expandedProject === project.id && (
                                  <div className="jobs-container">
                                    {isLoadingJobs ? (
                                      <div className="loading-jobs">
                                        <i className="fas fa-spinner fa-spin" />
                                        <span>Loading jobs...</span>
                                      </div>
                                    ) : projectJobs.length === 0 ? (
                                      <div className="no-jobs-message">
                                        <i className="fas fa-tasks" />
                                        <span>No jobs available</span>
                                      </div>
                                    ) : (
                                      projectJobs.map((job) => {
                                        const jobTasks = tasksData[job.id] || [];
                                        const isLoadingTasks = loadingTasks[job.id];
                                        
                                        return (
                                          <div key={job.id} className="job-node">
                                            <div
                                              className="job-item"
                                              onClick={() => handleJobClick(job.id)}
                                              onDoubleClick={(e) => handleJobDoubleClick(e, job)}
                                            >
                                              <i className={`fas fa-chevron-${expandedJob === job.id ? 'down' : 'right'} expand-icon`} />
                                              <i className="fas fa-tasks job-icon" />
                                              <span className="job-name">{job.name}</span>
                                              <span className="task-count">
                                                ({job.taskCount || jobTasks.length})
                                              </span>
                                            </div>
                                            
                                            {expandedJob === job.id && (
                                              <div className="tasks-container">
                                                {isLoadingTasks ? (
                                                  <div className="loading-tasks">
                                                    <i className="fas fa-spinner fa-spin" />
                                                    <span>Loading tasks...</span>
                                                  </div>
                                                ) : jobTasks.length === 0 ? (
                                                  <div className="no-tasks-message">
                                                    <i className="fas fa-check-square" />
                                                    <span>No tasks available</span>
                                                  </div>
                                                ) : (
                                                  jobTasks.map((task) => (
                                                    <div
                                                      key={task.id}
                                                      className="task-item"
                                                      onClick={() => handleTaskClick(task, job, project, workspace)}
                                                    >
                                                      <i className="fas fa-check-square task-icon" />
                                                      <span className="task-name">{task.name}</span>
                                                    </div>
                                                  ))
                                                )}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default WorkspaceTreeNav;